import { translateServerText } from '../lib/server-text';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { useApp } from '../lib/app-context';
import {
  MODE_LABEL,
  PAYMENT_HINT,
  PAYMENT_LABEL,
  addMonths,
  day,
  mad,
  nightsBetween,
  perUnit,
  today,
} from '../lib/format';
import { CopyValue, Field, Notice, Select, Spinner } from '../components/ui';
import type { Booking, PaymentMethod, PropertyDetail, Quote } from '../lib/types';
import { t } from '../lib/i18n';

/**
 * The booking flow: dates → your details → payment.
 *
 * The quote is fetched from the server on every change rather than computed here.
 * A price the client calculates is a price that can disagree with the one that gets
 * charged, and the commission and deposit rules live in one place on purpose.
 */

const STEPS = [t("Dates"), t("Coordonnées"), t("Paiement")] as const;

export default function BookingFlow() {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const { user, toast, reportError } = useApp();

  const roomId = params.get('roomId');
  const [step, setStep] = useState(0);
  const [property, setProperty] = useState<PropertyDetail | null>(null);
  const [mode, setMode] = useState(params.get('mode') ?? '');
  const [startDate, setStartDate] = useState(params.get('startDate') ?? today());
  const [endDate, setEndDate] = useState(params.get('endDate') ?? '');
  const [months, setMonths] = useState(10);
  const [guests, setGuests] = useState(1);
  const [guestMessage, setGuestMessage] = useState('');
  const [method, setMethod] = useState<PaymentMethod>('CARD_CMI');
  const [quote, setQuote] = useState<Quote | null>(null);
  const [quoting, setQuoting] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [booking, setBooking] = useState<Booking | null>(null);
  const [busy, setBusy] = useState(false);
  const [instructions, setInstructions] = useState<{ label: string; value: string }[] | null>(null);

  const selectedRoom = useMemo(
    () => property?.rooms.find((room) => room.id === roomId) ?? null,
    [property, roomId],
  );

  const availableModes = useMemo(() => {
    if (!property) return [];
    const source = selectedRoom?.rentalModes.length ? selectedRoom.rentalModes : property.rentalModes;
    return source.filter((rentalMode) => rentalMode.isActive);
  }, [property, selectedRoom]);

  const activeMode = availableModes.find((rentalMode) => rentalMode.mode === mode) ?? availableModes[0];
  const isNightly = activeMode?.unit === 'night';

  useEffect(() => {
    if (!id) return;
    void (async () => {
      try {
        const response = await api.get<{ property: PropertyDetail }>(`/properties/${id}`);
        setProperty(response.property);
        const modes = (
          roomId
            ? (response.property.rooms.find((room) => room.id === roomId)?.rentalModes ?? [])
            : []
        ).concat(response.property.rentalModes);
        const first = modes.find((item) => item.isActive);
        if (first && !params.get('mode')) setMode(first.mode);
        if (first) setMonths(Math.max(first.minStay, first.unit === 'month' ? first.minStay : 1));
      } catch (error) {
        reportError(error, t("Annonce introuvable"));
      }
    })();
  }, [id, roomId, params, reportError]);

  const effectiveEnd = useMemo(() => {
    if (isNightly) return endDate;
    return addMonths(startDate, months);
  }, [isNightly, endDate, startDate, months]);

  /**
   * Durations offered. Built from the listing's own minimum rather than a fixed
   * list: a place with a 4-month minimum must not show "1 mois", and the value
   * actually selected must always be present or the select silently renders
   * something other than what will be booked.
   */
  const durationOptions = useMemo(() => {
    const minimum = activeMode?.minStay ?? 1;
    const maximum = activeMode?.maxStay ?? 36;
    const candidates = new Set([minimum, months, 1, 3, 6, 9, 10, 12]);
    return [...candidates]
      .filter((count) => count >= minimum && count <= maximum)
      .sort((a, b) => a - b);
  }, [activeMode, months]);

  const fetchQuote = useCallback(async () => {
    if (!id || !activeMode || !startDate) return;
    if (isNightly && !endDate) return;
    setQuoting(true);
    setQuoteError(null);
    try {
      const response = await api.post<Quote>('/bookings/quote', {
        propertyId: id,
        ...(roomId ? { roomId } : {}),
        mode: activeMode.mode,
        startDate,
        ...(isNightly ? { endDate } : { durationMonths: months }),
        guests,
        paymentMethod: method,
      });
      setQuote(response);
    } catch (error) {
      setQuote(null);
      setQuoteError(error instanceof ApiError ? error.message : t("Impossible de calculer le prix"));
    } finally {
      setQuoting(false);
    }
  }, [id, activeMode, startDate, endDate, months, guests, isNightly, roomId, method]);

  useEffect(() => {
    void fetchQuote();
  }, [fetchQuote]);

  const createBooking = async () => {
    if (!id || !activeMode) return;
    setBusy(true);
    try {
      const response = await api.post<{ booking: Booking }>('/bookings', {
        propertyId: id,
        ...(roomId ? { roomId } : {}),
        mode: activeMode.mode,
        startDate,
        ...(isNightly ? { endDate } : { durationMonths: months }),
        guests,
        paymentMethod: method,
        ...(guestMessage ? { guestMessage } : {}),
      });
      setBooking(response.booking);
      setStep(2);
      toast(t("Demande envoyée à l’hôte."), 'ok');
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(false);
    }
  };

  const startPayment = async () => {
    if (!booking) return;
    setBusy(true);
    try {
      const response = await api.post<{
        checkoutUrl: string | null;
        instructions: { label: string; value: string }[] | null;
      }>(`/bookings/${booking.id}/payments`, { method });
      if (response.checkoutUrl) {
        // The fake provider's checkout is our own simulator, which posts a signed
        // webhook back — so the money path runs for real, minus the money.
        window.location.href = response.checkoutUrl;
        return;
      }
      setInstructions(response.instructions ?? []);
      toast(t("Instructions de paiement générées."), 'ok');
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(false);
    }
  };

  if (!property || !activeMode) {
    return (
      <div className="page center" style={{ paddingTop: 60 }}>
        <Spinner label={t("Chargement…")} />
      </div>
    );
  }

  const nights = isNightly && endDate ? nightsBetween(startDate, endDate) : 0;

  return (
    <div className="page page--mid stack stack--loose">
      <div>
        <Link to={`/logement/${property.id}`} className="small">
          {t("← Retour à l’annonce")}
        </Link>
        <h1 style={{ fontSize: 26, marginTop: 8 }}>{property.title}</h1>
        <p className="muted small" style={{ margin: 0 }}>
          {selectedRoom ? `${selectedRoom.name} · ` : ''}
          {property.neighborhood}, {t(property.city)}
        </p>
      </div>

      <div className="steps">
        {STEPS.map((label, index) => (
          <span key={label} className={`steps__bar ${index <= step ? 'steps__bar--done' : ''}`} />
        ))}
      </div>
      <span className="small muted" style={{ marginTop: -20 }}>
        {t("Étape")} {step + 1} {t("sur 3 —")} {STEPS[step]}
      </span>

      {step === 0 ? (
        <div className="card card--pad stack">
          <h2>{isNightly ? t("Quelles dates ?") : t("Quand emménagez-vous ?")}</h2>

          {availableModes.length > 1 ? (
            <Field label={t("Type de séjour")}>
              <Select
                value={activeMode.mode}
                onChange={(value) => setMode(value)}
                options={availableModes.map((item) => ({
                  value: item.mode,
                  label: `${MODE_LABEL[item.mode]} — ${mad(item.priceCentimes)}${perUnit(item.unit)}`,
                }))}
              />
            </Field>
          ) : null}

          <div className="row row--wrap" style={{ gap: 10, alignItems: 'flex-end' }}>
            <Field label={isNightly ? t("Arrivée") : t("Date d’emménagement")}>
              <input
                className="input"
                type="date"
                min={today()}
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
              />
            </Field>
            {isNightly ? (
              <Field label={t("Départ")} hint={nights > 0 ? t('{count} nuit(s)', { count: nights }) : undefined}>
                <input
                  className="input"
                  type="date"
                  min={startDate}
                  value={endDate}
                  onChange={(event) => setEndDate(event.target.value)}
                />
              </Field>
            ) : (
              <Field label={t("Durée du bail")} hint={t('Fin le {date}', { date: day(effectiveEnd) })}>
                <Select
                  value={String(months)}
                  onChange={(value) => setMonths(Number(value))}
                  options={durationOptions.map((count) => ({
                    value: String(count),
                    label: count === 10 ? t('10 mois (année universitaire)') : t('{count} mois', { count }),
                  }))}
                />
              </Field>
            )}
            <Field label={t("Voyageurs")}>
              <Select
                value={String(guests)}
                onChange={(value) => setGuests(Number(value))}
                options={Array.from(
                  { length: selectedRoom?.maxGuests ?? property.maxGuests },
                  (_, index) => ({
                    value: String(index + 1),
                    label: t('{count} personne(s)', { count: index + 1 }),
                  }),
                )}
              />
            </Field>
          </div>

          <Notice tone="info">
            {t("Bedly ne perçoit que ses frais de service, payés en ligne au moment de la réservation. Le loyer se règle en espèces directement à l’hôte, lors de la remise des clés.")}
          </Notice>

          {quoteError ? <Notice tone="danger">{quoteError}</Notice> : null}
          {quote && !quote.available ? (
            <Notice tone="danger">{t("Ces dates ne sont plus disponibles. Essayez d’autres dates.")}</Notice>
          ) : null}

          <QuoteSummary quote={quote} loading={quoting} />

          <button
            className="btn btn--block"
            disabled={!quote?.available || quoting}
            onClick={() => setStep(1)}
          >
            {t("Continuer")}
          </button>
        </div>
      ) : null}

      {step === 1 ? (
        <div className="card card--pad stack">
          <h2>{t("Vos coordonnées")}</h2>
          <div className="stack stack--tight">
            <div className="price-line">
              <span className="muted">{t("Nom")}</span>
              <span className="strong">{user?.fullName}</span>
            </div>
            <div className="price-line">
              <span className="muted">{t("Email")}</span>
              <span className="strong">{user?.email}</span>
            </div>
            {user?.phone ? (
              <div className="price-line">
                <span className="muted">{t("Téléphone")}</span>
                <span className="strong">{user.phone}</span>
              </div>
            ) : null}
          </div>

          {user?.kycStatus === 'APPROVED' ? (
            <Notice tone="ok">{t("CIN déjà vérifiée — rien à renvoyer.")}</Notice>
          ) : (
            <Notice tone="warn">
              {t("Votre identité n’est pas encore vérifiée. Vous pouvez réserver, mais la vérification rassure les hôtes et accélère les acceptations.")}{' '}
              <Link to="/verification">{t("Vérifier maintenant")}</Link>
            </Notice>
          )}

          <Field label={t("Message à l’hôte (optionnel)")}>
            <textarea
              className="textarea"
              rows={4}
              value={guestMessage}
              onChange={(event) => setGuestMessage(event.target.value)}
              placeholder={t("Présentez-vous en deux lignes : études, durée souhaitée, date de visite possible…")}
            />
          </Field>

          <QuoteSummary quote={quote} loading={quoting} />

          <div className="row" style={{ gap: 10 }}>
            <button className="btn btn--ghost" onClick={() => setStep(0)}>
              {t("Retour")}
            </button>
            <button className="btn" style={{ flex: 1 }} disabled={busy} onClick={createBooking}>
              {busy ? t("Envoi…") : t("Envoyer la demande")}
            </button>
          </div>
          <p className="tiny muted center" style={{ margin: 0 }}>
            {t("L’hôte a 48 h pour répondre. Les dates sont réservées pendant ce délai.")}
          </p>
        </div>
      ) : null}

      {step === 2 && booking ? (
        <div className="card card--pad stack">
          <div className="stack stack--tight">
            <h2>{t("Demande envoyée")}</h2>
            <p className="muted small" style={{ margin: 0 }}>
              {t("Référence")} <span className="strong">{booking.reference}</span> · {day(booking.startDate)} →{' '}
              {day(booking.endDate)}
            </p>
          </div>

          {booking.status === 'PENDING' ? (
            <Notice tone="warn">
              {t("En attente de la réponse de l’hôte. Vous pourrez régler les frais de service dès qu’il accepte — vous recevrez une notification.")}
            </Notice>
          ) : null}

          {booking.status === 'ACCEPTED' || booking.status === 'AWAITING_PAYMENT' ? (
            <>
              <h3>{t("Comment réglez-vous les frais de service ?")}</h3>
              <div className="stack stack--tight">
                {(Object.keys(PAYMENT_LABEL) as PaymentMethod[]).map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={`chip ${method === option ? 'chip--on' : ''}`}
                    style={{ justifyContent: 'flex-start', width: '100%', padding: '12px 14px' }}
                    onClick={() => setMethod(option)}
                  >
                    <span className="stack stack--tight" style={{ alignItems: 'flex-start' }}>
                      <span className="strong">{PAYMENT_LABEL[option]}</span>
                      <span className="tiny muted">{PAYMENT_HINT[option]}</span>
                    </span>
                  </button>
                ))}
              </div>
              <button className="btn btn--block" disabled={busy} onClick={startPayment}>
                {busy ? t("Préparation…") : t('Payer les frais de service ({amount})', { amount: mad(booking.amounts.platformFeeCentimes) })}
              </button>
            </>
          ) : null}

          {instructions ? (
            <div className="card card--pad stack stack--tight">
              <h3>{t("Instructions de paiement")}</h3>
              {instructions.map((line) => (
                <CopyValue key={line.label} label={translateServerText(line.label)} value={line.value} />
              ))}
              <p className="tiny muted" style={{ margin: 0 }}>
                {t("La réservation est confirmée dès réception du paiement. Vous recevrez une notification.")}
              </p>
            </div>
          ) : null}

          <div className="row" style={{ gap: 10 }}>
            <Link className="btn btn--ghost" to="/compte">
              {t("Mes réservations")}
            </Link>
            <Link className="btn" style={{ flex: 1 }} to={`/reservations/${booking.id}`}>
              {t("Voir la réservation")}
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function QuoteSummary({ quote, loading }: { quote: Quote | null; loading: boolean }) {
  if (loading && !quote) {
    return (
      <div className="card card--pad">
        <Spinner label={t("Calcul du prix…")} />
      </div>
    );
  }
  if (!quote) return null;

  const breakdown = quote.breakdown;
  return (
    <div className="card card--pad stack stack--tight">
      <div className="price-line">
        <span>
          {mad(breakdown.hostPriceCentimes)}
          <span className="muted">
            {' '}
            {t("×")} {breakdown.unit === 'night' ? t('{count} nuit(s)', { count: breakdown.periods }) : t('{count} mois', { count: breakdown.periods })}
          </span>
        </span>
        <span>{mad(breakdown.hostTotalCentimes)}</span>
      </div>
      <div className="price-line">
        <span>
          {t("Frais de service Bedly (")}{breakdown.feeBps / 100} %)
          <span className="price-line__note"> {t("· une seule fois, à la réservation")}</span>
        </span>
        <span>{mad(breakdown.platformFeeCentimes)}</span>
      </div>
      <div className="price-line price-line--total">
        <span>{t("Total du séjour")}</span>
        <span>{mad(breakdown.totalCentimes)}</span>
      </div>

      <div
        className="stack stack--tight"
        style={{ marginTop: 8, paddingTop: 10, borderTop: '1px solid var(--line)' }}
      >
        <div className="price-line">
          <span className="strong" style={{ color: 'var(--terra)' }}>
            {t("À payer maintenant en ligne")}
          </span>
          <span className="strong" style={{ color: 'var(--terra)' }}>
            {mad(breakdown.dueNowCentimes)}
          </span>
        </div>
        <div className="price-line">
          <span className="muted small">{t("En espèces, à l’hôte, à la remise des clés")}</span>
          <span className="small">{mad(breakdown.dueInCashCentimes)}</span>
        </div>
      </div>

      <p className="tiny muted" style={{ margin: '6px 0 0' }}>
        {t("Bedly ne perçoit que ses frais de service : le loyer est réglé directement à l’hôte, en espèces, lorsque vous vous rencontrez. Annulation :")} {' '}
        {quote.cancellationPolicy.tiers.map((tier) => translateServerText(tier.label)).join(' · ')}.
      </p>
    </div>
  );
}
